import React from 'react'
import { SPARTACUS_COLUMN_TOOLTIPS } from 'src/constants/spartacusTooltips'
import SpartacusOrderTrendCell from 'src/screens/spartacus/SpartacusOrderTrendCell'
import SpartacusPhoneCell from 'src/screens/spartacus/SpartacusPhoneCell'
import SpartacusScoreCell from 'src/screens/spartacus/SpartacusScoreCell'
import SpartacusSignalCell from 'src/screens/spartacus/SpartacusSignalCell'
import { ColumnHeaderWithTooltip } from 'src/utils/venueProductFieldTooltips'
import {
  formatPercentValue,
  formatSpartacusUkShortDate,
  formatYesNo,
  getTableColumnSortOrder,
  isSpartacusCountSignalActive,
  isSpartacusGpPercentSignalActive,
  isSpartacusYesNoSignalActive,
  getSpartacusActiveSuppliersTone,
} from 'src/utils/spartacusMappers'

export const SPARTACUS_TABLE_COLUMN_KEYS = [
  'venueName',
  'phone',
  'plan',
  'channel',
  'createdAt',
  'lastOrderedAt',
  'ordersInPeriod',
  'orderTrend',
  'upgradeScore',
  'riskScore',
  'activeSuppliers',
  'parLevelsUsed',
  'restrictedSuppliers',
  'specialPurchasesInPeriod',
  'recipesCount',
  'stockTakesCount',
  'xeroConnected',
  'gpUsing',
  'gpPercent',
]

function header(label, tooltipKey, { nowrap = false } = {}) {
  return (
    <ColumnHeaderWithTooltip
      label={label}
      tooltip={SPARTACUS_COLUMN_TOOLTIPS[tooltipKey]}
      nowrap={nowrap}
    />
  )
}

function renderCountSignal(value) {
  const label = value == null ? '0' : String(value)

  return (
    <SpartacusSignalCell
      label={label}
      isActive={isSpartacusCountSignalActive(value)}
    />
  )
}

function renderYesNoSignal(value) {
  return (
    <SpartacusSignalCell
      label={formatYesNo(value)}
      isActive={isSpartacusYesNoSignalActive(value)}
    />
  )
}

function renderGpPercentSignal(value) {
  return (
    <SpartacusSignalCell
      label={formatPercentValue(value, 1)}
      isActive={isSpartacusGpPercentSignalActive(value)}
    />
  )
}

function renderActiveSuppliersSignal(value) {
  const label = value == null ? '0' : String(value)

  return (
    <SpartacusSignalCell
      label={label}
      tone={getSpartacusActiveSuppliersTone(value)}
    />
  )
}

export function buildSpartacusTableColumns({ sortBy, sortDir }) {
  const sortOrder = columnKey =>
    getTableColumnSortOrder(columnKey, sortBy, sortDir)

  return [
    {
      title: header('Venue', 'venueName'),
      dataIndex: 'name',
      key: 'venueName',
      fixed: 'left',
      width: 200,
      sorter: true,
      sortOrder: sortOrder('venueName'),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: header('Phone', 'phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: value => <SpartacusPhoneCell phone={value} />,
    },
    {
      title: header('Plan', 'plan', { nowrap: true }),
      dataIndex: 'plan',
      key: 'plan',
      width: 108,
    },
    {
      title: header('Channel', 'channel', { nowrap: true }),
      dataIndex: 'channel',
      key: 'channel',
      width: 104,
    },
    {
      title: header('Created', 'createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      sortOrder: sortOrder('createdAt'),
      sortDirections: ['descend', 'ascend'],
      render: formatSpartacusUkShortDate,
    },
    {
      title: header('Last ordered', 'lastOrderedAt'),
      dataIndex: 'lastOrderedAt',
      key: 'lastOrderedAt',
      width: 120,
      sorter: true,
      sortOrder: sortOrder('lastOrderedAt'),
      sortDirections: ['descend', 'ascend'],
      render: formatSpartacusUkShortDate,
    },
    {
      title: header('Orders', 'ordersInPeriod'),
      dataIndex: 'ordersInPeriod',
      key: 'ordersInPeriod',
      width: 90,
      sorter: true,
      sortOrder: sortOrder('ordersInPeriod'),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: header('Order trend', 'orderTrend'),
      key: 'orderTrend',
      width: 150,
      sorter: true,
      sortOrder: sortOrder('orderTrend'),
      sortDirections: ['descend', 'ascend'],
      render: (_value, record) => <SpartacusOrderTrendCell venue={record} />,
    },
    {
      title: header('Upgrade score', 'upgradeScore'),
      dataIndex: 'upgradeScore',
      key: 'upgradeScore',
      width: 120,
      sorter: true,
      sortOrder: sortOrder('upgradeScore'),
      sortDirections: ['descend', 'ascend'],
      render: (value, record) => (
        <SpartacusScoreCell
          score={value}
          drivers={record.scoreDrivers}
          variant='upgrade'
        />
      ),
    },
    {
      title: header('Risk score', 'riskScore'),
      dataIndex: 'riskScore',
      key: 'riskScore',
      width: 110,
      sorter: true,
      sortOrder: sortOrder('riskScore'),
      sortDirections: ['descend', 'ascend'],
      render: (value, record) => (
        <SpartacusScoreCell
          score={value}
          drivers={record.riskScoreDrivers}
          variant='risk'
        />
      ),
    },
    {
      title: header('Active suppliers', 'activeSuppliers'),
      dataIndex: 'activeSuppliers',
      key: 'activeSuppliers',
      width: 120,
      render: renderActiveSuppliersSignal,
    },
    {
      title: header('Par levels', 'parLevelsUsed'),
      dataIndex: 'parLevelsUsed',
      key: 'parLevelsUsed',
      width: 100,
      render: renderCountSignal,
    },
    {
      title: header('Restricted suppliers', 'restrictedSuppliers'),
      dataIndex: 'restrictedSuppliers',
      key: 'restrictedSuppliers',
      width: 140,
      render: renderCountSignal,
    },
    {
      title: header('Special purchases', 'specialPurchasesInPeriod'),
      dataIndex: 'specialPurchasesInPeriod',
      key: 'specialPurchasesInPeriod',
      width: 130,
    },
    {
      title: header('Recipes', 'recipesCount'),
      dataIndex: 'recipesCount',
      key: 'recipesCount',
      width: 90,
      render: renderCountSignal,
    },
    {
      title: header('Stock takes', 'stockTakesCount'),
      dataIndex: 'stockTakesCount',
      key: 'stockTakesCount',
      width: 100,
      render: renderCountSignal,
    },
    {
      title: header('Xero', 'xeroConnected'),
      dataIndex: 'xeroConnected',
      key: 'xeroConnected',
      width: 80,
      render: renderYesNoSignal,
    },
    {
      title: header('GP using', 'gpUsing'),
      dataIndex: 'gpUsing',
      key: 'gpUsing',
      width: 90,
      render: renderYesNoSignal,
    },
    {
      title: header('GP %', 'gpPercent'),
      dataIndex: 'gpPercent',
      key: 'gpPercent',
      width: 80,
      render: renderGpPercentSignal,
    },
  ]
}
